/* eslint-disable */
import {
  MONITORING_INDICATORS_PATH,
  resolveMonitoringApiBase,
  resolveMonitoringExportPath,
  resolveMonitoringListPath,
} from '@/store/modules/integration-monitoring/integration-monitoring-config';
import { store as observableStore } from '@/shared/observable/store';
import HttpService from '../HttpService';

const MONITORING_REQUEST_CONFIG = {
  headers: { 'X-Feign': 'true' },
};

let suppressedDialogRequests = 0;
let previousCustomDialogState = false;

const withSuppressedGlobalErrorDialog = async (request) => {
  if (suppressedDialogRequests === 0) {
    previousCustomDialogState = observableStore.isCustomDialog;
    observableStore.isCustomDialog = true;
  }
  suppressedDialogRequests += 1;

  try {
    return await request();
  } finally {
    suppressedDialogRequests -= 1;
    if (suppressedDialogRequests === 0) {
      observableStore.isCustomDialog = previousCustomDialogState;
    }
  }
};

const FILTER_API_BASE = '/analytics-bff';

const buildQuery = (params = {}) => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return;
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== null && item !== undefined && item !== '') {
          searchParams.append(key, item);
        }
      });
      return;
    }

    searchParams.append(key, value);
  });

  const query = searchParams.toString().replace(/%2C/gi, ',');
  return query ? `?${query}` : '';
};

const normalizePaginatedResponse = (response) => {
  const data = response?.data ?? response ?? {};
  const rawContent = Array.isArray(data.content) ? data.content : [];
  const content = rawContent.map((item) => ({
    id: item.id ?? item.carrierId ?? item.contractId,
    name: item.name ?? item.description ?? item.legalName ?? String(item.id ?? ''),
  }));
  const page = typeof data.page === 'number' ? data.page : data.number ?? 0;
  const size = data.size ?? 20;
  const totalElements = data.totalElements ?? content.length;
  const last = typeof data.last === 'boolean'
    ? data.last
    : page * size + content.length >= totalElements;

  return {
    content,
    totalElements,
    page,
    size,
    last,
  };
};

export default class IntegrationMonitoringService {
  constructor() {
    this._monitoringHttpByBase = {};
    this._filterHttp = new HttpService(FILTER_API_BASE);
  }

  _getMonitoringHttp(processType) {
    const base = resolveMonitoringApiBase(processType);
    if (!this._monitoringHttpByBase[base]) {
      this._monitoringHttpByBase[base] = new HttpService(base);
    }
    return this._monitoringHttpByBase[base];
  }

  async fetchIndicators(params = {}, processType = 'COPARTICIPATION') {
    const monitoringHttp = this._getMonitoringHttp(processType);
    return withSuppressedGlobalErrorDialog(() => monitoringHttp.get(
      `${MONITORING_INDICATORS_PATH}${buildQuery(params)}`,
      null,
      MONITORING_REQUEST_CONFIG,
    ));
  }

  async fetchBatches(params = {}, pageable = {}, processType = 'COPARTICIPATION') {
    const monitoringHttp = this._getMonitoringHttp(processType);
    const listPath = resolveMonitoringListPath(processType);
    return withSuppressedGlobalErrorDialog(() => monitoringHttp.get(
      `${listPath}${buildQuery({ ...params, ...pageable })}`,
      null,
      MONITORING_REQUEST_CONFIG,
    ));
  }

  async exportBatch(batchId, processType = 'COPARTICIPATION') {
    const monitoringHttp = this._getMonitoringHttp(processType);
    return withSuppressedGlobalErrorDialog(() => monitoringHttp.get(
      resolveMonitoringExportPath(processType, batchId),
      null,
      { responseType: 'blob', ...MONITORING_REQUEST_CONFIG },
    ));
  }

  async fetchFinancialGroups(page, size, searchText) {
    const params = { page, size };

    if (searchText) {
      params.name = searchText;
    }

    const response = await withSuppressedGlobalErrorDialog(() => this._filterHttp.get(
      `/financial-group/paginated${buildQuery(params)}`,
      {},
    ));

    return normalizePaginatedResponse(response);
  }

  async fetchCarriers(page, size, searchText, financialGroupId) {
    const params = { allStates: true, page, size };

    if (searchText) {
      params.name = searchText;
    }

    if (financialGroupId !== null && financialGroupId !== undefined && financialGroupId !== '') {
      params.financialGroupIds = financialGroupId;
    }

    const response = await withSuppressedGlobalErrorDialog(() => this._filterHttp.get(
      `/carrier/paginated${buildQuery(params)}`,
      {},
    ));

    return normalizePaginatedResponse(response);
  }

  async fetchContracts(page, size, searchText, financialGroupId) {
    const params = { page, size };

    if (searchText) {
      params.searchByLegalNameOrPolicy = searchText;
    }

    if (financialGroupId !== null && financialGroupId !== undefined && financialGroupId !== '') {
      params.financialGroupId = financialGroupId;
    }

    const response = await withSuppressedGlobalErrorDialog(() => this._filterHttp.get(
      `/contract/paginated${buildQuery(params)}`,
      {},
    ));

    return normalizePaginatedResponse(response);
  }
}
